import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { getApp } from '@angular/fire/app';
import {
  Analytics, initializeAnalytics, logEvent, setDefaultEventParameters,
  setUserId, setUserProperties
} from '@angular/fire/analytics';
import { filter } from 'rxjs';
import { User } from '../data-models/user.model';

/**
 * Creates the Analytics instance. Used as the `provideAnalytics` factory in
 * app.config.ts, which only registers it when `environment.firebase.enableAnalytics`
 * is true — so with the flag off, nothing is initialised and nothing is sent.
 *
 * Google's policies forbid sending PII (emails, names, phone numbers) to GA, and
 * gtag attaches the full browser URL to every event by default. Several of our
 * URLs carry PII or credentials (`/reset-password?email=`,
 * `/accept-invitation?token=`, `/sign-in/:invitationId`), so:
 *  - automatic page views are off; AnalyticsService sends them with a sanitised URL;
 *  - a safe default `page_location` is set BEFORE init, so no event (including
 *    automatic ones like session_start / user_engagement) ever carries the raw URL.
 *
 * GA property settings this relies on (Admin → Data streams → Web → Enhanced
 * measurement): "Page changes based on browser history events" must be OFF, or
 * GA logs a second, unsanitised page view on every route change.
 */
export function createAnalytics(): Analytics {
  setDefaultEventParameters({ page_location: `${window.location.origin}/` });
  return initializeAnalytics(getApp(), {
    config: {
      send_page_view: false,
      // Signals + ad personalisation add cross-device ad tracking that would
      // need extra disclosure/consent; ADN gets nothing from either.
      allow_google_signals: false,
      allow_ad_personalization_signals: false
    }
  });
}

/**
 * The single point of contact with Google Analytics.
 *
 * Page views are reported by route TEMPLATE (`/customers/:id`), never by the live
 * URL — that keeps IDs, tokens and query strings out of GA, and also groups
 * reports per screen instead of one row per record.
 *
 * Every method is a no-op when analytics is disabled for the environment.
 */
@Injectable({
  providedIn: 'root'
})
export class AnalyticsService {
  private analytics = inject(Analytics, { optional: true });
  private router = inject(Router);

  constructor() {
    if (!this.analytics) return;

    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(() => this.trackPageView());
  }

  /**
   * Associate subsequent events with a signed-in user. Uses the Firebase UID — an
   * opaque id, which GA allows — never the email or name.
   */
  setUser(user: User): void {
    if (!this.analytics) return;
    setUserId(this.analytics, user.uid);
    setUserProperties(this.analytics, {
      // Lets reports filter out ADN's own staff traffic
      adn_staff: user.customClaims?.isAdmin() ? 'true' : 'false'
    });
  }

  /** Detach the user on sign-out so a shared browser isn't credited to them. */
  clearUser(): void {
    if (!this.analytics) return;
    setUserId(this.analytics, null);
    setUserProperties(this.analytics, { adn_staff: null });
  }

  /**
   * Log a custom event. Params must never contain PII (emails, names, phone
   * numbers, addresses) or free text a user typed.
   */
  logEvent(name: string, params?: Record<string, string | number | boolean>): void {
    if (!this.analytics) return;
    logEvent(this.analytics, name, params);
  }

  private trackPageView(): void {
    if (!this.analytics) return;
    const pageLocation = `${window.location.origin}${this.routeTemplate()}`;
    const pageTitle = document.title;

    // Update the default first so automatic events on this page carry the
    // sanitised URL too, then send the page view itself.
    setDefaultEventParameters({ page_location: pageLocation, page_title: pageTitle });
    logEvent(this.analytics, 'page_view', { page_location: pageLocation, page_title: pageTitle });
  }

  /** e.g. `/sign-in/:invitationId` for `/sign-in/abc123?email=x@y.com` */
  private routeTemplate(): string {
    const segments: string[] = [];
    let route: ActivatedRouteSnapshot | null = this.router.routerState.snapshot.root;
    while (route) {
      const path = route.routeConfig?.path;
      if (path) segments.push(path === '**' ? '(not-found)' : path);
      route = route.firstChild;
    }
    return '/' + segments.join('/');
  }
}
